import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import ConfirmModal from '../components/ConfirmModal';
import MaterialIcon from '../components/MaterialIcon';
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from '../services/api';
import { toast } from 'react-toastify';

const Admins = ({ setIsAuthenticated, darkMode, toggleDarkMode }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'admin',
    permissions: []
  });

  // All available permissions (id must match Layout allNavItems)
  const availablePermissions = [
    { id: 'workflows', label: 'Workflows', icon: 'graph_1' },
    { id: 'webhooks', label: 'Webhooks', icon: 'webhook' },
    { id: 'ai-connectors', label: 'AI Connectors', icon: 'smart_toy' },
    { id: 'admins', label: 'Admins', icon: 'person' },
  ];

  useEffect(() => {
    loadAdmins();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };

    if (showModal) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [showModal]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await getAdmins();
      setAdmins(response.admins || []);
    } catch (error) {
      console.error('Error loading admins:', error);
      toast.error('Error loading admins');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || (!editingAdmin && !formData.password)) {
      toast.error('Fill in all required fields');
      return;
    }

    try {
      if (editingAdmin) {
        // Update existing admin
        const updateData = {
          username: formData.username,
          role: formData.role,
          permissions: formData.permissions
        };
        // Only update password if provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        await updateAdmin(editingAdmin._id, updateData);
        toast.success('Admin updated successfully');
      } else {
        // Create new admin
        await createAdmin(formData);
        toast.success('Admin created successfully');
      }
      
      setShowModal(false);
      setEditingAdmin(null);
      setFormData({ username: '', password: '', role: 'admin', permissions: [] });
      loadAdmins();
    } catch (error) {
      console.error('Error saving admin:', error);
      toast.error(error.response?.data?.error || 'Error saving admin');
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      username: admin.username,
      password: '',
      role: admin.role || 'admin',
      permissions: admin.permissions || []
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setAdminToDelete(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!adminToDelete) return;

    try {
      await deleteAdmin(adminToDelete);
      toast.success('Admin removed successfully');
      loadAdmins();
      setAdminToDelete(null);
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.error || 'Error removing admin');
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => {
      const permissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId];
      return { ...prev, permissions };
    });
  };

  const handleSelectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: availablePermissions.map(p => p.id)
    }));
  };

  const handleDeselectAllPermissions = () => {
    setFormData(prev => ({
      ...prev,
      permissions: []
    }));
  };

  const openCreateModal = () => {
    setEditingAdmin(null);
    setFormData({ username: '', password: '', role: 'admin', permissions: [] });
    setShowModal(true);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super-admin': return 'badge-danger';
      case 'admin': return 'badge-info';
      case 'viewer': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super-admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'viewer': return 'Viewer';
      default: return role;
    }
  };

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="spinner"></div>
      </Layout>
    );
  }

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="page-header">
        <h1>
          <MaterialIcon icon="person" size={28} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          Admin Management
        </h1>
        <p>Manage administrators and their access rights</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Admin list ({admins.length})</h3>
          <button onClick={openCreateModal} className="btn btn-primary">
            <MaterialIcon icon="add" size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Add admin
          </button>
        </div>

        {admins.length === 0 ? (
          <div className="empty-state">
            <h3>No admins</h3>
            <p>Add the first admin by clicking the button above</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Access rights</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map(admin => (
                <tr key={admin._id}>
                  <td>
                    <strong>{admin.username}</strong>
                  </td>
                  <td>
                    <span className={`badge ${getRoleBadgeClass(admin.role)}`}>
                      {getRoleLabel(admin.role)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {(admin.permissions || []).map(permId => {
                        const perm = availablePermissions.find(p => p.id === permId);
                        return perm ? (
                          <span key={permId} className="badge badge-info" style={{ fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <MaterialIcon icon={perm.icon} size={14} />
                            {perm.label}
                          </span>
                        ) : null;
                      })}
                      {(!admin.permissions || admin.permissions.length === 0) && (
                        <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem' }}>
                          No rights
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{new Date(admin.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(admin)}
                      >
                        <MaterialIcon icon="edit" size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(admin._id)}
                      >
                        <MaterialIcon icon="delete" size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAdmin ? 'Edit admin' : 'Add admin'}</h2>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password {!editingAdmin && '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={editingAdmin ? 'Leave empty to keep current' : 'Enter password'}
                  required={!editingAdmin}
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="viewer">Viewer</option>
                  <option value="admin">Admin</option>
                  <option value="super-admin">Super Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Access rights</label>
                <div style={{ marginBottom: '15px' }}>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-secondary" 
                    onClick={handleSelectAllPermissions}
                    style={{ marginRight: '10px' }}
                  >
                    <MaterialIcon icon="check_box" size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    All
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-secondary" 
                    onClick={handleDeselectAllPermissions}
                  >
                    <MaterialIcon icon="check_box_outline_blank" size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    None
                  </button>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px',
                  maxHeight: '250px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  backgroundColor: '#f8f9fa'
                }}>
                  {availablePermissions.map(permission => (
                    <label 
                      key={permission.id} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        backgroundColor: formData.permissions.includes(permission.id) ? '#e3f2fd' : 'white',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        if (!formData.permissions.includes(permission.id)) {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!formData.permissions.includes(permission.id)) {
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission.id)}
                        style={{ marginRight: '10px', cursor: 'pointer', width: 'auto' }}
                      />
                      <span style={{ fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MaterialIcon icon={permission.icon} size={18} />
                        {permission.label}
                      </span>
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#7f8c8d' }}>
                  Selected: {formData.permissions.length} / {availablePermissions.length}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAdmin ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setAdminToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Remove admin"
        message="Are you sure you want to remove this admin?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="btn-danger"
      />
    </Layout>
  );
};

export default Admins;
